<template>
	<v-chart
		class="chart"
		:option="option"
		ref="chart"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>




<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
// https://echarts.apache.org/handbook/en/concepts/dataset/#
import VChart from 'vue-echarts';
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { Post } from '@/models/post.js'
import { useStationsStore } from '@/stores/StationsStore'
import { INFLUXDB, readStationMetrics, readPostReportTable } from '@/api/api.js'
import { chartDateStr, chartTimeStr } from '@/helpers.js'

import { use } from 'echarts/core'
import {
	LineChart
} from 'echarts/charts'
import {
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent
} from 'echarts/components'
import {
	CanvasRenderer,
	SVGRenderer,
} from 'echarts/renderers'

use([
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent,
	LineChart,
	CanvasRenderer,
	SVGRenderer,
])

let xAxisYearTmp = null

export default {
	// Line indexes
	stationEi: 0,
	// Colors
	TITLE_COLOR: '#2c3e50',
	//
	RED: '#f2495c',
	ORANGE: '#ff9830',
	BLUE: '#5794f2',
	//
	STATION_POTENTIAL_AXIS_NAME: 'СКЗ',
	POST_POTENTIAL_AXIS_NAME: 'КВП',
	//
	POST_POTENTIAL_SERIA_TEMPLATE: {
		name: 'КВП',
		type: 'line',
		xAxisIndex: 0,
		yAxisIndex: 0,
		symbol: 'circle',
		symbolSize: 6	,
		showSymbol: true,
		//showAllSymbol: true,
		data: [],
		smooth: false,
		lineStyle: {
			width: 2,
			//type: 'dotted',
			opacity: 1,
		},
		itemStyle: {
			borderWidth: 20,
		},
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},
		posts: {
			type: Array,
			require: false,
			default: new Array(),
		},
		timeFrom: {
			type: Date,
			require: false,
			default: () => {
				const now = new Date()
				now.setDate(now.getDate() - 1)
				//now.setHours(now.getHours() - 4)
				return now
			},
		},
		timeTo: {
			type: Date,
			require: false,
			default: new Date(),
		},
	},

	components: {
		VChart,
	},

	setup() {
		const stationsStore = useStationsStore()

		return {
			stationsStore,
		}
	},

	data() {
		return {
			// Graph configuration
			option: {
				title: [
					{
						//top: '0%',
						left: 'center',
						text: 'Потенціали станції та КВП',
						textStyle: {
							color: this.$options.TITLE_COLOR,
							fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
							fontStyle: 'normal',
							fontSize: 14.28,
							fontWeight: 700,
						},
						textAlign: 'auto',
						padding: [8, 100],
					}
				],
				legend: {
					left: 'center',
					bottom: 5,
					selectedMode: 'muultiple', // can select multiple lines for show
				},
				grid: [ // need for padding control (make smaller padding)
					{ left: 60, right: 30, top: 60, bottom: 60 },
				],
				tooltip: {
					trigger: 'axis',
					// Tooltip builder function
					formatter: (params/*, ticket, callback*/) => {
						const parseData = (data) => data === 'NaN' ? '-' : data

						// Get first valid date
						let date = new Date()
						for (let index = 0; index < params.length; index++) {
							if (params[index]) {
								date = new Date(new Date(params[index].axisValue).getTime())
								break
							}
						}

						// Addtooltip header
						let tooltip = `<div class="container">`
						tooltip += `<div class="row"><div class="col text-center">`
						tooltip += ` ${chartDateStr(date)} ${chartTimeStr(date)}`
						tooltip += `</div></div>`

						// Add tooltip body
						for (let index = 0; index < params.length; index++) {
							const parm = params[index];
							tooltip += `<div class="row justify-content-between">`
							tooltip += `<div class="col ms-0 text-start"><span style="color: ${parm.color};">&#9210;</span> ${parm.seriesName}</div>`
							tooltip += `<div class="col-auto ms-auto me-0 text-end fw-bolder">${parseData(parm.data[1])}</div>`
							tooltip += `</div>`
						}

						tooltip += `</div>`
						return tooltip
					}
				},
				xAxis: [
					{
						gridIndex: 0, // attach to grid
						type: 'time',
						boundaryGap: false,
						data: [],
						axisLabel: {
							hideOverlap: true,
							formatter: (value, /*idx*/) => {
								var local = new Date(new Date(value).getTime())
								
								// If first axis label in chart or first label in year
								if( (xAxisYearTmp == null
									|| local.getFullYear() != xAxisYearTmp.getFullYear())
								) {
									xAxisYearTmp = local
									return `${local.getFullYear()}`
								} else {
									// Make date string
									return chartDateStr(local)
								}
							}
						},
					}
				],
				yAxis: [
					// Potential
					{
						gridIndex: 0,
						type: 'value',
						name: 'E',
						position: 'left',
						alignTicks: false,
						min: -5,
						max: 2,
						// Vertical line of axis
						axisLine: {
							show: true,
							lineStyle: {
								color: this.$options.TITLE_COLOR,
							}
						},
						// Grid lines of axis
						splitLine: {
							show: true,
						},
						axisLabel: {
							formatter: '{value}'
						},
						axisPointer: {
							snap: true
						}
					},
				],
				// Add style to lines
				visualMap: [
					// Station potential
					{
						type: 'piecewise',
						show: false,
						seriesIndex: this.$options.stationEi,
						top: 50,
						right: 1200,
						pieces: [
							{
								lt: 0,
								gte: -0.85,
								color: this.$options.RED,
							},
							{
								lt: -0.85,
								gte: -2.5,
								color: this.$options.BLUE,
							},
							{
								lt: -2.5,
								gte: -3.5,
								color: this.$options.ORANGE,
							},
							{
								lt: -3.5,
								gte: -5,
								color: this.$options.RED,
							},
						],
						outOfRange: {
							color: this.$options.RED,
						},
					},
				],
				series: [
					// Station potential
					{
						name: this.$options.STATION_POTENTIAL_AXIS_NAME,
						type: 'line',
						xAxisIndex: 0,
						yAxisIndex: 0,
						data: [],
						smooth: false,
						lineStyle: {
							width: 1,
						},
						showSymbol: false,
						areaStyle: { // enable area gradient (by visual map)
							opacity: 0.04,
						},
					},
				]
			}
		}
	},

	watch: {
		// Update chart on station id update
		stationId(/*newCriterias*/) {
			//console.log('Update stationId')
			//this.readValues()
		},
		posts() {
			this.readValues()
		},
		// Update chart on timeFrom update
		timeFrom(/*newTime, oldTime*/) {
			//console.log('Update time from', oldTime, newTime)
			this.readValues()
		},
		// Update chart on timeTo update
		timeTo(/*newCriterias*/) {
			//console.log('Update time to')
			this.readValues()
		},
	},

	async created() {
		//console.log('props: ', this.timeFrom, this.timeTo)
		//const voltage = 
		await this.readValues()
		//console.log("Fetch voltage: ", voltage)
	},

	mounted() {
		
	},

	methods: {
		async readValues() {
			// Fetch station metrics
			let arr = []

			let unitNames = []
			let metricNames = []
			if (this.station.uProtectSource == 'power') {
				unitNames.push(INFLUXDB.station_metrics.power_unit)
				metricNames.push(PowerUnit.metricKeys.eOut)
			} else if (this.station.uProtectSource == 'service') {
				unitNames.push(INFLUXDB.station_metrics.service_unit)
				metricNames.push(ServiceUnit.metricKeys.eAvg)
			} else {
				this.option.series.splice(this.$options.stationEi, 1)
				return
			}

			await readStationMetrics(
				this.station.passport.get(Station.passportKeys.metricsId),
				unitNames, 
				metricNames,
				arr,
				this.timeFrom,
				this.timeTo,
				undefined,
				['median', 'min',],
				15000,
			).then(result => {
				if (!result) {
					return
				}

				//console.log("Fetched: ", arr)
				this.option.xAxis[this.$options.stationEi].data = arr.map((el) => el.time)
				if (this.station.uProtectSource == 'power') {
					this.option.series[this.$options.stationEi].data = arr.map(
						(el) => [el.time, Number(el[INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.eOut + '_median']).toFixed(2),]
					)
				} else if (this.station.uProtectSource == 'service') {
					this.option.series[this.$options.stationEi].data = arr.map(
						(el) => [el.time, Number(el[INFLUXDB.station_metrics.service_unit + '_' + ServiceUnit.metricKeys.eAvg + '_median']).toFixed(2)]
					)
				}
			}).catch(err => {
				console.error("Error on read station potentials: ", err)
			})

			// Iteate all related posts
			this.posts.forEach(async (post, index) => {
				// console.log(`Post[${index}] = ${post.metrics_id}, tome_from = ${this.timeFrom.toISOString()}, tome_to = ${this.timeTo.toISOString()}`)
				const postSerieIndex = index + 1
				if (this.option.series[postSerieIndex] == undefined) {
					this.option.series.push({ ...this.$options.POST_POTENTIAL_SERIA_TEMPLATE})
				}
				
				// Fetch current post metrics
				const postMetrics = []
				await readPostReportTable(
					post.metrics_id,
					this.timeFrom,
					this.timeTo,
					postMetrics
				).then(result => {
					if (!result) {
						return
					}
					
					this.option.series[postSerieIndex].name = `${this.$options.POST_POTENTIAL_AXIS_NAME} "${this.posts[index].name}"`
					this.option.xAxis[this.$options.stationEi].data = postMetrics.map((el) => el.time)
					this.option.series[postSerieIndex].data = postMetrics.map((el) => [el.time, Number(el[Post.metricsKeys.Eavg]).toFixed(2),])
				}).catch(err => {
					console.error("Error on read post potentials: ", err)
					delete this.option.series.splice(postSerieIndex, 1)
				})

				this.option.xAxis[this.$options.stationEi].data.sort(
					(a, b) => a.getTime() > b.getTime()
				)
			})

			// console.log('Series', this.option.series)

			return arr
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId); },

		chartInstance() {
			return this.$refs.chart?.chart
		},
	},
}
</script>




<style scoped>
.chart {
	height: 100%;
}
</style>